import React, { useState, useEffect, useRef } from 'react';
import {
  Settings,
  Save,
  Loader2,
  FileText,
  Image,
  Eye,
  Building2,
  Hash,
  Receipt,
  QrCode,
  Type,
  Minus,
  Plus,
  RotateCcw,
  Palette
} from 'lucide-react';
import { getReceiptConfig, updateReceiptConfig, getReceiptPreview } from '../../api/receipts';
import { getBrandingConfig } from '../../api/brandingSettings';
import { toast } from 'react-toastify';
import { clearReceiptCache } from '../../utils/receiptImageCache';
import LoadingSpinner from '../../components/common/LoadingSpinner';

/**
 * ReceiptSettings Component
 * Admin interface for customizing receipt templates and settings
 * Features side-by-side preview and auto-updating
 */
const ReceiptSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewBlob, setPreviewBlob] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('receipt'); // 'receipt' or 'payslip'
  const [brandingConfig, setBrandingConfig] = useState(null);
  const [config, setConfig] = useState({
    use_company_info: true,
    company_name: '',
    company_address: '',
    company_phone: '',
    company_email: '',
    company_website: '',
    tax_id: '',
    tax_rate: 0,
    tax_label: 'VAT',
    template_id: 1,
    template_theme: 'black-white-gold',
    show_logo: true,
    show_company_details: true,
    show_tax_breakdown: false,
    show_payment_details: true,
    show_customer_details: true,
    show_referrer_details: true,
    show_tax_number: false,
    show_barcode: false,
    show_qr_code: false,
    // Colors
    bg_color: '#ffffff',
    text_color: '#111827',
    accent_color: '#D4AF37',
    heading_color: '#ffffff',
    label_color: '#8c7731',
    use_branding_colors: false,
    // Font
    font_weight: 'normal',
    font_sizes: {},
    footer_text: '',
    terms_and_conditions: '',
    currency_symbol: '₦',
    currency_code: 'NGN',
    paper_size: 'A5',
    email_enabled: false,
    email_subject_template: '',
    email_body_template: '',
    delivery_format: 'auto',
    require_transfer_approval: true,
    whatsapp_message_template:
      'Hello {customer_name},\n\nThank you for your purchase at {company_name}!\n\n📄 Receipt Number: {receipt_number}\n💰 Total Amount: ₦{total_amount}\n\nFor any inquiries, please contact us.\n\nThank you for your business!\n\n- {company_name} Team',

    // Payslip Settings defaults
    payslip_template_id: 1,
    payslip_delivery_format: 'auto',
    payslip_show_logo: true,
    payslip_show_company_details: true,
    payslip_show_customer_details: true,
    payslip_show_payment_details: true,
    payslip_show_product_images: false,
    payslip_footer_text: 'Thank you for your hard work!',
    payslip_footer_notes: '',
    payslip_terms_and_conditions: '',
    payslip_show_tax_number: false,
    payslip_show_barcode: false,
    payslip_show_qr_code: false,
    payslip_whatsapp_template:
      '📋 *Payslip for {pay_period}*\n\nHello {employee_name},\n\nPlease find attached your payslip for {pay_period}.\n\n💵 Net Pay: {net_pay}\n\n- {company_name} Team',
    payslip_email_subject_template: 'Payslip for {pay_period} - {company_name}',
    payslip_email_body_template:
      'Dear {employee_name},\n\nPlease find your payslip for {pay_period} attached.\n\nNet Pay: {net_pay}\n\nBest regards,\n{company_name}'
  });

  // Debounce ref
  const timeoutRef = useRef(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  // Cleanup blob on unmount
  useEffect(() => {
    return () => {
      if (previewBlob) URL.revokeObjectURL(previewBlob);
    };
  }, [previewBlob]);

  // Auto-refresh preview on config change
  useEffect(() => {
    if (!loading) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      timeoutRef.current = setTimeout(() => {
        refreshPreview();
      }, 800); // 800ms debounce
    }
  }, [
    activeTab,
    config.template_id,
    config.payslip_template_id,
    config.paper_size,
    config.company_name,
    config.show_logo,
    config.show_company_details,
    config.show_customer_details,
    config.show_payment_details,
    config.payslip_show_logo,
    config.payslip_show_company_details,
    config.payslip_show_customer_details,
    config.payslip_show_payment_details,
    config.footer_text,
    config.payslip_footer_text,
    config.currency_symbol
  ]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const [receiptRes, brandingRes] = await Promise.all([
        getReceiptConfig(),
        getBrandingConfig().catch(() => ({ data: null }))
      ]);
      setConfig(receiptRes.data);
      if (brandingRes?.data) {
        setBrandingConfig(brandingRes.data);
      }
      // Initial preview load
      refreshPreview(receiptRes.data);
    } catch (error) {
      toast.error('Failed to load receipt configuration');
    } finally {
      setLoading(false);
    }
  };

  // Cache PDF.js library reference to avoid repeated dynamic imports
  const pdfLibRef = useRef(null);

  const refreshPreview = async (currentConfig = config) => {
    setPreviewLoading(true);
    try {
      const startTime = Date.now();
      const blob = await getReceiptPreview({ ...currentConfig, documentType: activeTab });
      console.log(`[Preview] PDF fetched in ${Date.now() - startTime}ms`);

      // Cache PDF.js library reference
      if (!pdfLibRef.current) {
        const pdfjsLib = await import('pdfjs-dist');
        // Use CDN for worker (reliable across all environments)
        pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
        pdfLibRef.current = pdfjsLib;
        console.log('[Preview] PDF.js library loaded');
      }
      const pdfjsLib = pdfLibRef.current;

      const renderStart = Date.now();
      const arrayBuffer = await blob.arrayBuffer();
      const loadingTask = pdfjsLib.getDocument({
        data: arrayBuffer,
        isEvalSupported: false,
        useSystemFonts: true
      });
      const pdfDoc = await loadingTask.promise;
      const page = await pdfDoc.getPage(1);

      // Lower scale = faster rendering (1.0x for fastest preview)
      const scale = 1.0;
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;

      // Convert to JPEG blob URL (faster than base64 and more memory efficient)
      const imageBlob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
      const imageUrl = URL.createObjectURL(imageBlob);
      await pdfDoc.destroy();
      console.log(`[Preview] Rendered in ${Date.now() - renderStart}ms`);

      setPreviewBlob((prev) => {
        if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
        return imageUrl;
      });
    } catch (error) {
      console.error('Preview generation failed:', error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setConfig((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await updateReceiptConfig(config);
      // Clear cached receipt images since settings changed
      clearReceiptCache();
      toast.success('Receipt configuration updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading settings..." className="min-h-screen" />;
  }

  return (
    <div className="min-h-screen bg-secondary-offWhite p-4 lg:p-6">
      <div className="max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Settings className="w-8 h-8 text-accent-gold" />
              <div>
                <h1 className="text-2xl font-bold text-primary-black">Receipt Settings</h1>
                <p className="text-secondary-gray text-sm mt-1">
                  Customize templates, company info, and delivery preferences
                </p>
              </div>
            </div>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="flex items-center justify-center gap-2 px-6 py-2.5 bg-accent-gold text-white rounded-lg hover:bg-accent-gold/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
            >
              {saving ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Toggle Settings Mode */}
        <div className="bg-white rounded-lg shadow-sm mb-6 overflow-hidden">
          <div className="flex border-b border-secondary-lightGray">
            {[
              { id: 'receipt', label: 'Receipt Settings', icon: Receipt },
              { id: 'payslip', label: 'Payslip Settings', icon: FileText }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-accent-gold text-white border-b-2 border-accent-gold'
                      : 'text-secondary-gray hover:bg-secondary-offWhite hover:text-primary-black'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ROW 1: Template Design + Live Preview side-by-side */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
          {/* Template Selection */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-lg shadow-sm p-6 h-full">
              <h2 className="text-lg font-semibold text-primary-black mb-4 border-b pb-2">
                Template Design
              </h2>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                {(activeTab === 'payslip'
                  ? [
                      { id: 1, name: 'Zoho Modern', desc: 'Elegant Split Layout' },
                      { id: 2, name: 'Payroll Hero', desc: 'Compact Industrial' },
                      { id: 3, name: 'Vertex42 Clean', desc: 'Spreadsheet Invoice' },
                      { id: 4, name: 'Zoho Classic', desc: 'Structured Columns' },
                      { id: 5, name: 'Bee Creative', desc: 'Modern Color-Blocked' }
                    ]
                  : [
                      { id: 1, name: 'Standard', desc: 'Classic Business' },
                      { id: 2, name: 'Modern', desc: 'Clean & Bold' },
                      { id: 3, name: 'Bold', desc: 'High Contrast' },
                      { id: 4, name: 'Minimal', desc: 'Whitespace' },
                      { id: 5, name: 'Invoice', desc: 'Professional' },
                      { id: 6, name: 'Detailed', desc: 'Boxed Grid' },
                      { id: 7, name: 'Elegant', desc: 'Serif Luxury' },
                      { id: 8, name: 'Corporate', desc: 'Formal Blue' }
                    ]
                ).map((template) => {
                  const fieldName = activeTab === 'payslip' ? 'payslip_template_id' : 'template_id';
                  const isSelected = (config[fieldName] || 1) === template.id;
                  return (
                    <label
                      key={template.id}
                      className={`relative flex flex-col p-3 border-2 rounded-xl cursor-pointer transition-all hover:shadow-md ${
                        isSelected
                          ? 'border-accent-gold bg-accent-gold/5 ring-1 ring-accent-gold/20'
                          : 'border-secondary-lightGray hover:border-accent-gold/50'
                      }`}
                    >
                      <input
                        type="radio"
                        name={fieldName}
                        value={template.id}
                        checked={isSelected}
                        onChange={() => handleInputChange(fieldName, template.id)}
                        className="sr-only"
                      />
                      <div className="bg-white border border-secondary-lightGray rounded h-20 mb-3 flex items-center justify-center overflow-hidden">
                        <span className="text-2xl font-bold text-gray-200">#{template.id}</span>
                      </div>
                      <span className="font-semibold text-sm text-primary-black">
                        {template.name}
                      </span>
                      <span className="text-xs text-secondary-gray">{template.desc}</span>
                      {isSelected && (
                        <div className="absolute top-2 right-2 w-5 h-5 bg-accent-gold rounded-full flex items-center justify-center">
                          <span className="text-white text-xs">✓</span>
                        </div>
                      )}
                    </label>
                  );
                })}
              </div>

              {/* Format & Size */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-primary-black mb-2">
                    Paper Size
                  </label>
                  <div className="flex bg-gray-100 p-1 rounded-lg">
                    {(activeTab === 'payslip' ? ['A4'] : ['A5', 'A6', '80mm']).map((size) => {
                      const currentSize = activeTab === 'payslip' ? 'A4' : config.paper_size;
                      return (
                        <button
                          key={size}
                          type="button"
                          onClick={() =>
                            activeTab !== 'payslip' && handleInputChange('paper_size', size)
                          }
                          className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                            currentSize === size
                              ? 'bg-accent-gold text-white shadow-sm'
                              : 'text-secondary-gray hover:text-primary-black'
                          }`}
                        >
                          {size}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary-black mb-2">
                    Default Delivery Format
                  </label>
                  <div className="flex bg-gray-100 p-1 rounded-lg">
                    {[
                      { val: 'auto', label: 'Auto', icon: Settings },
                      { val: 'pdf', label: 'PDF', icon: FileText },
                      { val: 'image', label: 'Image', icon: Image }
                    ].map((fmt) => {
                      const deliveryField =
                        activeTab === 'payslip' ? 'payslip_delivery_format' : 'delivery_format';
                      const currentVal = config[deliveryField] || 'auto';
                      return (
                        <button
                          key={fmt.val}
                          type="button"
                          onClick={() => handleInputChange(deliveryField, fmt.val)}
                          className={`flex-1 py-2 text-sm font-medium rounded-md transition-all flex items-center justify-center gap-1 ${
                            currentVal === fmt.val
                              ? 'bg-accent-gold text-white shadow-sm'
                              : 'text-secondary-gray hover:text-primary-black'
                          }`}
                          title={fmt.val === 'auto' ? 'Best fit for device' : fmt.label}
                        >
                          <fmt.icon className="w-3.5 h-3.5" />
                          {fmt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Live Preview Panel */}
          <div className="lg:col-span-5">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-secondary-lightGray h-full">
              <div className="bg-primary-charcoal px-4 py-3 flex items-center justify-between">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <Eye className="w-4 h-4 text-accent-gold" />
                  Live Preview
                </h3>
                {previewLoading && (
                  <span className="flex items-center gap-2 text-xs text-accent-gold">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Updating...
                  </span>
                )}
              </div>

              <div className="p-4 bg-secondary-offWhite min-h-[400px] flex items-center justify-center overflow-auto">
                {previewBlob ? (
                  <img
                    src={previewBlob}
                    alt="Receipt Preview"
                    className={`shadow-2xl transition-opacity duration-300 ${previewLoading ? 'opacity-70' : 'opacity-100'}`}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '60vh',
                      objectFit: 'contain'
                    }}
                  />
                ) : (
                  <div className="flex flex-col items-center text-secondary-gray">
                    <Loader2 className="w-8 h-8 animate-spin mb-2" />
                    <p>Generating preview...</p>
                  </div>
                )}
              </div>

              <div className="px-4 py-2 bg-white border-t border-secondary-lightGray text-xs text-center text-secondary-gray">
                Actual output may vary slightly depending on PDF viewer.
              </div>
            </div>
          </div>
        </div>

        {/* ROW 2: Remaining Settings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Company Info */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4 border-b pb-2">
              <h2 className="text-lg font-semibold text-primary-black flex items-center gap-2">
                <Building2 className="w-5 h-5 text-accent-gold" />
                Company Information
              </h2>
              <div className="flex items-center gap-2">
                <span className="text-xs text-secondary-gray font-medium">
                  Use General Settings
                </span>
                <button
                  type="button"
                  onClick={() =>
                    handleInputChange(
                      'use_company_info',
                      config.use_company_info === false ? true : false
                    )
                  }
                  className={`relative w-9 h-5 rounded-full transition-colors ${config.use_company_info !== false ? 'bg-accent-gold' : 'bg-gray-200'}`}
                >
                  <div
                    className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${config.use_company_info !== false ? 'translate-x-4' : 'translate-x-0'}`}
                  />
                </button>
              </div>
            </div>

            {config.use_company_info !== false && (
              <div className="mb-4 p-3 rounded-lg border border-accent-gold/30 bg-accent-gold/5">
                <p className="text-xs text-accent-gold font-medium">
                  Using company info from Branding / General Settings
                </p>
                <p className="text-[11px] text-secondary-gray mt-0.5">
                  Company name, address, phone, and email are automatically synced from your main
                  Branding Settings. Turn off to enter custom override details for receipts &
                  payslips.
                </p>
              </div>
            )}

            <div
              className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${config.use_company_info !== false ? 'opacity-60 pointer-events-none' : ''}`}
            >
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-primary-black mb-1">
                  Company Name
                </label>
                <input
                  type="text"
                  value={
                    config.use_company_info !== false
                      ? brandingConfig?.company_name || config.company_name
                      : config.company_name
                  }
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  readOnly={config.use_company_info !== false}
                  className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent outline-none"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-primary-black mb-1">Address</label>
                <textarea
                  value={
                    config.use_company_info !== false
                      ? brandingConfig?.company_address || config.company_address
                      : config.company_address
                  }
                  onChange={(e) => handleInputChange('company_address', e.target.value)}
                  rows={2}
                  readOnly={config.use_company_info !== false}
                  className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent outline-none resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-primary-black mb-1">Phone</label>
                <input
                  type="text"
                  value={
                    config.use_company_info !== false
                      ? brandingConfig?.company_phone || config.company_phone
                      : config.company_phone
                  }
                  onChange={(e) => handleInputChange('company_phone', e.target.value)}
                  readOnly={config.use_company_info !== false}
                  className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-primary-black mb-1">Email</label>
                <input
                  type="text"
                  value={
                    config.use_company_info !== false
                      ? brandingConfig?.company_email || config.company_email
                      : config.company_email
                  }
                  onChange={(e) => handleInputChange('company_email', e.target.value)}
                  readOnly={config.use_company_info !== false}
                  className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent outline-none"
                />
              </div>
            </div>
          </div>

          {/* Display Options */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-primary-black mb-4 border-b pb-2 flex items-center gap-2">
              <Eye className="w-5 h-5 text-accent-gold" />
              Display Options
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-6">
              {(activeTab === 'payslip'
                ? [
                    { key: 'payslip_show_logo', label: 'Show Company Logo', icon: Image },
                    {
                      key: 'payslip_show_company_details',
                      label: 'Show Company Details',
                      icon: Building2
                    },
                    { key: 'payslip_show_tax_number', label: 'Show Tax Number', icon: Hash },
                    {
                      key: 'payslip_show_payment_details',
                      label: 'Show Payment Details',
                      icon: FileText
                    },
                    {
                      key: 'payslip_show_customer_details',
                      label: 'Show Employee Details',
                      icon: Building2
                    },
                    { key: 'payslip_show_barcode', label: 'Show Barcode', icon: Hash },
                    { key: 'payslip_show_qr_code', label: 'Show QR Code', icon: QrCode }
                  ]
                : [
                    { key: 'show_logo', label: 'Show Company Logo', icon: Image },
                    { key: 'show_company_details', label: 'Show Company Details', icon: Building2 },
                    { key: 'show_tax_number', label: 'Show Tax Number', icon: Hash },
                    { key: 'show_tax_breakdown', label: 'Show Tax Breakdown', icon: Receipt },
                    { key: 'show_payment_details', label: 'Show Payment Details', icon: FileText },
                    {
                      key: 'show_customer_details',
                      label: 'Show Customer Details',
                      icon: Building2
                    },
                    {
                      key: 'show_referrer_details',
                      label: 'Show Referrer Details',
                      icon: Building2
                    },
                    { key: 'show_barcode', label: 'Show Barcode', icon: Hash },
                    { key: 'show_qr_code', label: 'Show QR Code', icon: QrCode }
                  ]
              ).map(({ key, label, icon: Icon }) => (
                <label
                  key={key}
                  className="flex items-center justify-between p-2.5 border border-secondary-lightGray rounded-lg hover:bg-secondary-offWhite transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-2.5">
                    <Icon className="w-3.5 h-3.5 text-secondary-gray" />
                    <span className="text-xs font-medium text-primary-black">{label}</span>
                  </div>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={config[key] || false}
                      onChange={(e) => handleInputChange(key, e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-gray-200 rounded-full peer-checked:bg-accent-gold transition-colors" />
                    <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform peer-checked:translate-x-4" />
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Receipt Colors */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-primary-black mb-4 border-b pb-2 flex items-center gap-2">
              <Palette className="w-5 h-5 text-accent-gold" />
              Receipt Colors
            </h2>

            <label className="flex items-center justify-between p-2.5 border border-secondary-lightGray rounded-lg mb-4 cursor-pointer hover:bg-secondary-offWhite transition-colors">
              <span className="text-sm font-medium text-primary-black">Use Branding</span>
              <div className="relative">
                <input
                  type="checkbox"
                  checked={config.use_branding_colors || false}
                  onChange={(e) => handleInputChange('use_branding_colors', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-gray-200 rounded-full peer-checked:bg-accent-gold transition-colors" />
                <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform peer-checked:translate-x-4" />
              </div>
            </label>

            <div className="grid grid-cols-2 gap-3">
              {[
                { key: 'bg_color', label: 'Background', default: '#ffffff' },
                { key: 'accent_color', label: 'Accent', default: '#D4AF37' },
                { key: 'text_color', label: 'Text', default: '#111827' },
                { key: 'heading_color', label: 'Headings', default: '#ffffff' },
                { key: 'label_color', label: 'Labels', default: '#8c7731' }
              ].map((color) => (
                <div key={color.key} className="space-y-1">
                  <label className="block text-xs font-medium text-secondary-gray">
                    {color.label}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={config[color.key] || color.default}
                      onChange={(e) => handleInputChange(color.key, e.target.value)}
                      className="w-8 h-8 rounded border border-secondary-lightGray cursor-pointer"
                    />
                    <input
                      type="text"
                      value={config[color.key] || ''}
                      onChange={(e) => handleInputChange(color.key, e.target.value)}
                      placeholder={color.default}
                      className="flex-1 px-2 py-1.5 text-xs border border-secondary-lightGray rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent outline-none"
                    />
                    {config[color.key] && config[color.key] !== color.default && (
                      <button
                        type="button"
                        onClick={() => handleInputChange(color.key, color.default)}
                        className="text-secondary-gray hover:text-primary-black"
                        title="Reset"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-secondary-gray mt-3">
              Leave color fields empty for auto-contrast defaults. Leave accent empty for template
              default.
            </p>
          </div>

          {/* Font Weight */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-primary-black mb-4 border-b pb-2 flex items-center gap-2">
              <Type className="w-5 h-5 text-accent-gold" />
              Font Weight
            </h2>
            <div className="flex bg-gray-100 p-1 rounded-lg">
              {[
                { val: '300', label: 'Light' },
                { val: 'normal', label: 'Normal' },
                { val: '500', label: 'Medium' },
                { val: '600', label: 'Semi-Bold' },
                { val: 'bold', label: 'Bold' }
              ].map((w) => (
                <button
                  key={w.val}
                  type="button"
                  onClick={() => handleInputChange('font_weight', w.val)}
                  className={`flex-1 py-2 text-xs font-medium rounded-md transition-all ${
                    (config.font_weight || 'normal') === w.val
                      ? 'bg-accent-gold text-white shadow-sm'
                      : 'text-secondary-gray hover:text-primary-black'
                  }`}
                  style={{ fontWeight: w.val }}
                >
                  {w.label}
                </button>
              ))}
            </div>
          </div>

          {/* Font Sizes */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4 border-b pb-2">
              <h2 className="text-lg font-semibold text-primary-black flex items-center gap-2">
                <Type className="w-5 h-5 text-accent-gold" />
                Font Sizes
              </h2>
              <button
                type="button"
                onClick={() => handleInputChange('font_sizes', {})}
                className="text-xs text-secondary-gray hover:text-accent-gold flex items-center gap-1 transition-colors"
              >
                <RotateCcw className="w-3 h-3" />
                Reset All
              </button>
            </div>

            <div className="space-y-3">
              {[
                { key: 'company_name', label: 'Company Name', default: 14 },
                { key: 'company_details', label: 'Address / Details', default: 6 },
                { key: 'receipt_info', label: 'Receipt Info', default: 7 },
                { key: 'items', label: 'Item Rows', default: 8 },
                { key: 'totals', label: 'Subtotals', default: 8 },
                { key: 'total_highlight', label: 'Grand Total', default: 10 },
                { key: 'payment', label: 'Payment Info', default: 8 },
                { key: 'footer', label: 'Footer', default: 7 }
              ].map((size) => {
                const currentVal = config.font_sizes?.[size.key] || size.default;
                const isCustom = config.font_sizes?.[size.key] !== undefined;
                return (
                  <div key={size.key} className="flex items-center justify-between">
                    <span className="text-xs font-medium text-primary-black w-28">
                      {size.label}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          const newSizes = { ...(config.font_sizes || {}) };
                          newSizes[size.key] = Math.max(4, currentVal - 1);
                          handleInputChange('font_sizes', newSizes);
                        }}
                        className="w-7 h-7 flex items-center justify-center border border-secondary-lightGray rounded-md hover:bg-secondary-offWhite transition-colors"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="w-8 text-center text-sm font-semibold text-primary-black">
                        {currentVal}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          const newSizes = { ...(config.font_sizes || {}) };
                          newSizes[size.key] = Math.min(30, currentVal + 1);
                          handleInputChange('font_sizes', newSizes);
                        }}
                        className="w-7 h-7 flex items-center justify-center border border-secondary-lightGray rounded-md hover:bg-secondary-offWhite transition-colors"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                      <span className="text-xs text-secondary-gray w-5">px</span>
                      {isCustom && (
                        <button
                          type="button"
                          onClick={() => {
                            const newSizes = { ...(config.font_sizes || {}) };
                            delete newSizes[size.key];
                            handleInputChange('font_sizes', newSizes);
                          }}
                          className="text-secondary-gray hover:text-accent-gold transition-colors"
                          title="Reset to default"
                        >
                          <RotateCcw className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer & Terms */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-primary-black mb-4 border-b pb-2 flex items-center gap-2">
              <FileText className="w-5 h-5 text-accent-gold" />
              Footer & Terms
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-primary-black mb-1">
                  Footer Text
                </label>
                <textarea
                  value={
                    activeTab === 'payslip'
                      ? config.payslip_footer_text || ''
                      : config.footer_text || ''
                  }
                  onChange={(e) =>
                    handleInputChange(
                      activeTab === 'payslip' ? 'payslip_footer_text' : 'footer_text',
                      e.target.value
                    )
                  }
                  rows={2}
                  placeholder={
                    activeTab === 'payslip'
                      ? 'Thank you for your hard work!'
                      : 'Thank you for your business!'
                  }
                  className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent outline-none resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-primary-black mb-1">
                  Terms & Conditions
                </label>
                <textarea
                  value={
                    activeTab === 'payslip'
                      ? config.payslip_terms_and_conditions || ''
                      : config.terms_and_conditions || ''
                  }
                  onChange={(e) =>
                    handleInputChange(
                      activeTab === 'payslip'
                        ? 'payslip_terms_and_conditions'
                        : 'terms_and_conditions',
                      e.target.value
                    )
                  }
                  rows={3}
                  className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent outline-none resize-none"
                />
              </div>
            </div>
          </div>

          {/* Inventory & Transfers */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-primary-black mb-4 border-b pb-2">
              Inventory & Transfers
            </h2>
            <div className="space-y-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.require_transfer_approval}
                  onChange={(e) => handleInputChange('require_transfer_approval', e.target.checked)}
                  className="w-4 h-4 text-accent-gold rounded focus:ring-accent-gold"
                />
                <div>
                  <span className="text-sm font-medium text-primary-black block">
                    Require Stock Transfer Approval
                  </span>
                  <span className="text-xs text-secondary-gray">
                    If enabled, stock transfers must be approved by an admin before shipping. If
                    disabled, transfers are auto-approved.
                  </span>
                </div>
              </label>
            </div>
          </div>

          {/* Email Settings */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4 border-b pb-2">
              <h2 className="text-lg font-semibold text-primary-black">Email Settings</h2>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.email_enabled}
                  onChange={(e) => handleInputChange('email_enabled', e.target.checked)}
                  className="w-4 h-4 text-accent-gold rounded focus:ring-accent-gold"
                />
                <span className="text-sm font-medium">Enable Email</span>
              </label>
            </div>

            {config.email_enabled && (
              <div className="space-y-4 animate-in fade-in slide-in-from-top-4 duration-300">
                <div>
                  <label className="block text-sm font-medium text-primary-black mb-1">
                    {activeTab === 'payslip' ? 'Payslip Email Subject' : 'Subject Template'}
                  </label>
                  <input
                    type="text"
                    value={
                      activeTab === 'payslip'
                        ? config.payslip_email_subject_template || ''
                        : config.email_subject_template || ''
                    }
                    onChange={(e) =>
                      handleInputChange(
                        activeTab === 'payslip'
                          ? 'payslip_email_subject_template'
                          : 'email_subject_template',
                        e.target.value
                      )
                    }
                    placeholder={
                      activeTab === 'payslip'
                        ? 'Payslip for {pay_period} - {company_name}'
                        : 'Receipt #{receipt_number} - {company_name}'
                    }
                    className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:ring-2 focus:ring-accent-gold outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-primary-black mb-1">
                    Body Template
                  </label>
                  <textarea
                    value={
                      activeTab === 'payslip'
                        ? config.payslip_email_body_template || ''
                        : config.email_body_template || ''
                    }
                    onChange={(e) =>
                      handleInputChange(
                        activeTab === 'payslip'
                          ? 'payslip_email_body_template'
                          : 'email_body_template',
                        e.target.value
                      )
                    }
                    rows={4}
                    className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:ring-2 focus:ring-accent-gold outline-none resize-none"
                  />
                  <p className="text-xs text-secondary-gray mt-1">
                    {activeTab === 'payslip' ? (
                      <>
                        Available vars: {'{employee_name}, {pay_period}, {net_pay}, {company_name}'}
                      </>
                    ) : (
                      <>
                        Available vars:{' '}
                        {'{receipt_number}, {customer_name}, {company_name}, {total_amount}'}
                      </>
                    )}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* WhatsApp Message Template */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-primary-black mb-4 border-b pb-2 flex items-center gap-2">
              <svg className="w-5 h-5 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2.546 20.2a1 1 0 001.254 1.254l3.032-.892A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 2a8 8 0 110 16 8 8 0 010-16z"
                />
              </svg>
              WhatsApp Message Template
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-primary-black mb-1">
                  {activeTab === 'payslip'
                    ? 'Payslip WhatsApp Message'
                    : 'Receipt WhatsApp Message'}
                </label>
                <textarea
                  value={
                    activeTab === 'payslip'
                      ? config.payslip_whatsapp_template || ''
                      : config.whatsapp_message_template || ''
                  }
                  onChange={(e) =>
                    handleInputChange(
                      activeTab === 'payslip'
                        ? 'payslip_whatsapp_template'
                        : 'whatsapp_message_template',
                      e.target.value
                    )
                  }
                  rows={8}
                  placeholder="Enter your custom WhatsApp message template..."
                  className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:ring-2 focus:ring-accent-gold outline-none resize-none font-mono text-sm"
                />
                <p className="text-xs text-secondary-gray mt-2">
                  {activeTab === 'payslip' ? (
                    <>
                      Available placeholders:{' '}
                      <code className="bg-gray-100 px-1 rounded">{'{employee_name}'}</code>,{' '}
                      <code className="bg-gray-100 px-1 rounded">{'{pay_period}'}</code>,{' '}
                      <code className="bg-gray-100 px-1 rounded">{'{net_pay}'}</code>,{' '}
                      <code className="bg-gray-100 px-1 rounded">{'{company_name}'}</code>
                    </>
                  ) : (
                    <>
                      Available placeholders:{' '}
                      <code className="bg-gray-100 px-1 rounded">{'{customer_name}'}</code>,{' '}
                      <code className="bg-gray-100 px-1 rounded">{'{receipt_number}'}</code>,{' '}
                      <code className="bg-gray-100 px-1 rounded">{'{total_amount}'}</code>,{' '}
                      <code className="bg-gray-100 px-1 rounded">{'{company_name}'}</code>
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptSettings;
